/**
 * Session Timeout Modal Component
 *
 * Displays a warning when the user's session is about to expire.
 * Provides options to extend the session or logout.
 *
 * This modal should be mounted at the app root level so it's always available.
 */
import { Component, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SessionService } from '../../../core/services/session.service';
import { AuthService } from '../../../core/services/auth.service';
import { ButtonComponent } from '../button/button.component';

@Component({
    selector: 'app-session-timeout-modal',
    standalone: true,
    imports: [CommonModule, ButtonComponent],
    template: `
    @if (sessionService.showTimeoutWarning()) {
      <!-- Backdrop -->
      <div
        class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-timeout-title"
      >
        <!-- Modal -->
        <div
          class="bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-fade-in"
          role="alertdialog"
        >
          <!-- Icon -->
          <div class="flex justify-center mb-4">
            <div class="w-16 h-16 rounded-full bg-warning/10 flex items-center justify-center">
              <svg
                class="w-8 h-8 text-warning"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>

          <!-- Content -->
          <div class="text-center mb-6">
            <h2 id="session-timeout-title" class="text-xl font-semibold text-neutral-900 mb-2">
              Session Expiring Soon
            </h2>
            <p class="text-neutral-600">
              Your session will expire in
              <span class="font-semibold text-warning">{{ minutesDisplay() }}</span>
              due to inactivity.
            </p>
            <p class="text-sm text-neutral-500 mt-2">
              Would you like to stay logged in?
            </p>
          </div>

          <!-- Actions -->
          <div class="flex gap-3">
            <app-button
              variant="outline"
              [fullWidth]="true"
              (clicked)="onLogout()"
            >
              Logout
            </app-button>
            <app-button
              variant="primary"
              [fullWidth]="true"
              (clicked)="onStayLoggedIn()"
            >
              Stay Logged In
            </app-button>
          </div>
        </div>
      </div>
    }
  `,
    styles: [`
    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: scale(0.95);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }

    .animate-fade-in {
      animation: fadeIn 0.2s ease-out;
    }
  `],
})
export class SessionTimeoutModalComponent {
    protected sessionService = inject(SessionService);
    private authService = inject(AuthService);

    // Format minutes remaining for display
    minutesDisplay = computed(() => {
        const minutes = this.sessionService.minutesRemaining();
        if (minutes <= 1) {
            // Show seconds when under a minute
            const seconds = Math.ceil(this.sessionService.timeUntilTimeout() / 1000);
            return `${seconds} seconds`;
        }
        return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    });

    onStayLoggedIn(): void {
        this.sessionService.extendSession();
    }

    async onLogout(): Promise<void> {
        await this.authService.logout();
    }
}
